/* Product facts are extracted only from explicit catalog wording. Missing facts stay hidden. */
(function (scope) {
  'use strict';

  const defaults = {
    form: 'Product form', quantity: 'Quantity', packWeight: 'Pack weight', shippingSize: 'Size at dispatch',
    rooting: 'Root condition', pot: 'Container', selection: 'Selected option', light: 'Light', water: 'Water',
    soil: 'Soil', germination: 'Germination', sowing: 'Sowing / planting', temperature: 'Temperature',
    matureHeight: 'Mature size', hardiness: 'Hardiness zone', shippingForm: 'Shipping form', seeds: 'Seeds', plant: 'Live plant',
    cutting: 'Cuttings', leaf: 'Propagation leaves', stem: 'Propagation stems', bulb: 'Bulbs / tubers / rhizomes', rooted: 'Rooted', unrooted: 'Unrooted',
    bareRoot: 'Bare root', potIncluded: 'Pot included', potNotIncluded: 'Pot not included'
  };
  const number = String.raw`\d[\d,]*(?:\.\d+)?`;
  const measure = String.raw`${number}(?:\s*[-–—]\s*${number})?(?:\s*-\s*|\s*)(?:inches|inch|in\b|cm\b|centimet(?:er|re)s?\b|feet\b|foot\b|ft\b|["”″′])`;

  function plainText(value) {
    const entities = { amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', nbsp: ' ', ndash: '–', mdash: '—', rsquo: '’', lsquo: '‘' };
    return String(value || '').replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
      .replace(/<\/(?:p|div|li|h[1-6]|tr|section)>|<br\s*\/?\s*>/gi, '\n')
      .replace(/<[^>]*>/g, ' ').replace(/&(#x[0-9a-f]+|#\d+|\w+);/gi, (whole, code) => {
        if (code[0] !== '#') return entities[code.toLowerCase()] || whole;
        const point = code[1].toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
        return point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : '';
      }).replace(/[✔✓☑•]+[️]?/g, '\n').replace(/[\t\r ]+/g, ' ').replace(/ *\n */g, '\n').replace(/\n{2,}/g, '\n').trim();
  }

  function formsIn(text) {
    text = text.replace(/\b(?:not|no|without|rather than|instead of)\s+(?:an?\s+)?(?:(?:live|established)\s+)?(?:seeds?|plants?|seedlings?|cuttings?|bulbs?|tubers?|rhizomes?|corms?)\b/gi, '');
    const forms = [];
    // Seed-starting soil and accessories are not seeds, even when their intended use names seedlings.
    if (/\b(?:plant (?:stands?|labels?|supports?|clips?|lights?)|watering cans?|grow lights?|seed trays?|potting\s+(?:soil|mix)|soil\s+mix|perlite|vermiculite|fertili[sz]er|seed[ -]?starting?\s+(?:tray|kit|mix)|grow(?:ing)?\s+medium)\b/i.test(text)) return forms;
    if (/\bseeds?\b/i.test(text)) forms.push('seeds');
    const propagation = /\bpropagat(?:ion|e|ing)\b/i.test(text);
    if (propagation && /\b(?:leaves|leaf)\b/i.test(text)) forms.push('leaf');
    if (propagation && /\bstems?\b/i.test(text)) forms.push('stem');
    if (!forms.includes('leaf') && !forms.includes('stem') && /\bcuttings?\b/i.test(text)) forms.push('cutting');
    if (/\b(?:bulbs?|tubers?|rhizomes?|corms?)\b/i.test(text)) forms.push('bulb');
    if (!forms.length && /\b(?:plants?|seedlings?|pups?)\b|\blive\s+(?:flowering\s+)?(?:shrubs?|trees?)\b/i.test(text)) forms.push('plant');
    return forms;
  }

  function classify(data) {
    const title = plainText(data.title);
    if (/\b(?:plant (?:stands?|labels?|supports?|clips?|lights?)|watering cans?|grow lights?|seed trays?|potting\s+(?:soil|mix)|soil\s+mix|perlite|vermiculite|fertili[sz]er|seed[ -]?starting?\s+(?:tray|kit|mix)|grow(?:ing)?\s+medium)\b/i.test(title)) return 'other';
    const titleForms = formsIn(title);
    if (titleForms.length) return titleForms.length > 1 ? 'mixed' : titleForms[0];
    const typeForms = formsIn(plainText(data.productType));
    return typeForms.length > 1 ? 'mixed' : typeForms[0] || 'other';
  }

  function uniqueMatches(text, expression, value) {
    return [...new Set(Array.from(text.matchAll(expression), match => value ? value(match) : match[0].trim()))];
  }

  function only(values) { return values.length === 1 ? values[0] : ''; }

  function hasAmountChoices(text) {
    return new RegExp(`${number}\\s*(?:(?:seeds?|plants?|trees?|cuttings?|bulbs?|g|grams?|oz)\\s*)?(?:or|/)\\s*${number}`, 'i').test(text);
  }

  function quantity(text, kind) {
    if (hasAmountChoices(text)) return '';
    const units = { seeds: 'seeds?', plant: 'plants?|seedlings?|pups?|trees?|shrubs?', cutting: 'cuttings?', leaf: 'leaves|leaf cuttings?', stem: 'stems?|stem cuttings?', bulb: 'bulbs?|tubers?|rhizomes?|corms?' };
    const unit = units[kind];
    if (!unit) return '';
    const count = `${number}(?:\\s*[-–—]\\s*${number})?\\+?`;
    const pattern = new RegExp(`\\b((?:${number}\\s*(?:[x×]|packs?\\s+of)\\s*)?${count})\\s+((?:[\"'‘“]?[a-z][a-z’'\\-]*[\"'’”]?\\s+){0,8}?)(${unit})\\b`, 'gi');
    const matches = uniqueMatches(text, pattern, m => {
      if (/\b(?:mg|g|kg|grams?|milligrams?|oz|ounces?|gal|gallons?|qt|quarts?|liters?|litres?|pints?|inch|inches|in|cm|feet|ft|days?|weeks?|months?|years?|packets?|packs?|pots?|containers?|bags?|to|or|per|each|for)\b/i.test(m[2])) return '';
      if (/\b(?:seeds?|plants?|seedlings?|pups?|cuttings?|leaves|leaf|stems?|bulbs?|tubers?|rhizomes?|corms?|selected|from|contains|including|produces)\b/i.test(m[2])) return '';
      const qualifier = text.slice(Math.max(0, m.index - 15), m.index).match(/(?:approximately|approx\.?|about|around|at least)\s*$/i)?.[0].trim();
      return `${qualifier ? qualifier + ' ' : ''}${m[1]} ${m[3].toLowerCase()}`;
    }).filter(Boolean);
    return only(matches);
  }

  function packWeight(text) {
    const expression = new RegExp(`\\b(?:${number}\\s*(?:[x×]|(?:pcs|pieces?|packets?|packs?)\\s+(?:of|each containing))\\s*)?${number}\\s*(?:mg|milligrams?|g|grams?|kg|kilograms?|oz|ounces?)\\b`, 'gi');
    return only(uniqueMatches(text, expression));
  }

  function rooting(text, labels) {
    if (/\bunrooted\b|\bnot\s+rooted\b/i.test(text)) return labels.unrooted;
    if (/\brooted\b/i.test(text)) return labels.rooted;
    return '';
  }

  function shippingSize(text, kind, isTitle) {
    if (!['plant', 'cutting', 'bulb'].includes(kind)) return '';
    if (/\b(?:mature|maturity|adult|grows?\s+(?:up\s+)?to|can\s+reach|maximum|max\.?\s+height)\b/i.test(text)) return '';
    const cue = /\b(?:shipp?ed|shipping|dispatch|arrival|receive|delivered|current\s+(?:height|size))\b/i;
    if (!isTitle && !cue.test(text)) return '';
    if (kind === 'bulb' && !cue.test(text)) return '';
    const heightOnly = uniqueMatches(text, new RegExp(`\\b${measure}\\s*(?:tall|high|long)\\b`, 'gi'));
    if (heightOnly.length === 1) return heightOnly[0];
    const matches = uniqueMatches(text, new RegExp(`\\b${measure}(?:\\s*(?:tall|high|height|long|length))?`, 'gi'));
    // A pot measurement is not a plant height. Multiple sizes require the full description.
    if (/\bpot|\bcontainer/i.test(text) || matches.length !== 1) return '';
    return matches[0];
  }

  const careFields = [
    ['light', '(?:light(?: requirements?| needs)?|sunlight|sun exposure|sun requirements?)'],
    ['water', '(?:water(?:ing)?(?: requirements?| needs)?)'],
    ['soil', '(?:soil(?: type| requirements?)?)'],
    ['germination', '(?:germination(?: time| period| duration)?|days to germination)'],
    ['sowing', '(?:sowing(?: time| season| depth)?|planting(?: time| season| depth)?)'],
    ['temperature', '(?:(?:germination|growing|ideal|optimal) temperature|temperature(?: range)?)'],
    ['matureHeight', '(?:mature (?:height|size|spread)|height at maturity)'],
    ['hardiness', '(?:(?:USDA )?hardiness zones?|USDA zones?)']
  ];
  const extraLabels = '(?:common names?|scientific name|botanical name|plant type|growth stage|plant stage|foliage(?: color)?|bloom (?:time|color|season)|color|features|growth (?:rate|habit)|container friendly|care tip|humidity|use|ideal for|maintenance|time from sowing to bloom|days to maturity|plant spacing|pot size|size|format|volume|type|ships from|vendor)';
  const allCareLabels = careFields.map(field => field[1]).concat(extraLabels).join('|');

  function careFacts(description, labels) {
    const facts = [];
    const boundary = new RegExp(`\\b(?:${allCareLabels})\\s*[:：]`, 'gi');
    const fields = Array.from(description.matchAll(boundary));
    for (const [key, labelPattern] of careFields) {
      const matchingLabel = new RegExp(`^${labelPattern}\\s*[:：]$`, 'i');
      const values = [...new Set(fields.flatMap((field, index) => {
        if (!matchingLabel.test(field[0])) return [];
        const end = fields[index + 1]?.index ?? description.length;
        const value = description.slice(field.index + field[0].length, end).split('\n')[0].replace(/\s*[;•]\s*$/, '').trim();
        // Preserve short, labeled facts. Long prose stays in the source description with its full context.
        if (!value || value.length > 150 || /[.!?]\s+\S/.test(value) || /\bguarantee[ds]?\b|\b100\s*%\b/i.test(value)) return [];
        return [value];
      }))];
      const value = only(values);
      if (value) facts.push({ key, label: labels[key], value });
    }
    return facts;
  }

  function receiveSource(rawDescription) {
    const marker = /\b(?:you\s+(?:will\s+)?(?:receive|get)|(?:package|pack|order)\s+(?:contains|includes)|(?:shipped|sold|supplied)\s+(?:as|in|with)|shipping\s+(?:size|height)|size\s+at\s+dispatch)\b/i;
    const blocks = Array.from(String(rawDescription || '').matchAll(/<(p|h[1-6]|ul|ol)\b[^>]*>[\s\S]*?<\/\1>/gi), match => ({ type: match[1].toLowerCase(), text: plainText(match[0]) }));
    if (blocks.length) {
      const collected = [];
      for (const [index, block] of blocks.entries()) {
        const start = block.text.search(marker);
        if (start < 0) continue;
        collected.push(block.text.slice(start));
        // A receipt heading can describe the immediately following list, but never later care paragraphs.
        const next = blocks[index + 1];
        if (/\b(?:receive|get|contains|includes)\s*[:：]?$/i.test(block.text) && next && ['ul', 'ol'].includes(next.type)) collected.push(next.text);
      }
      return collected.join('\n');
    }
    // Plain-text exports have no paragraph structure. Keep only explicit receipt/shipping lines.
    return plainText(rawDescription).split('\n').filter(line => marker.test(line)).join('\n');
  }

  function parseProductFacts(input) {
    const data = input || {};
    const labels = Object.assign({}, defaults, data.labels || {});
    const title = plainText(data.title);
    const description = plainText(data.description);
    const kind = classify(data);
    const variant = data.selectedVariant || null;
    const variantTitle = plainText(variant?.title);
    const variantText = variantTitle === 'Default Title' ? '' : variantTitle;
    const receive = [];
    const add = (key, value) => { if (value) receive.push({ key, label: labels[key], value }); };
    if (labels[kind] && kind !== 'other' && kind !== 'mixed') add('form', labels[kind]);
    const receiveText = receiveSource(data.description);
    const receiveLines = receiveText.split('\n').filter(Boolean);
    const options = Array.isArray(variant?.options) ? variant.options : [];
    const optionNames = Array.isArray(data.optionNames) ? data.optionNames : [];
    let variantCount = quantity(variantText, kind);
    if (!variantCount && kind === 'seeds') {
      const countIndex = optionNames.findIndex(name => /^(?:seeds? count|number of seeds|seeds? quantity)$/i.test(String(name).trim()));
      if (countIndex >= 0 && /^\d[\d,]*$/.test(String(options[countIndex] || '').trim())) variantCount = `${String(options[countIndex]).trim()} seeds`;
    }
    // An explicit variant selection takes precedence over a base title's quantity/weight.
    const variantHasAmount = Boolean(variantCount || packWeight(variantText));
    const ambiguousAmount = hasAmountChoices(title) || hasAmountChoices(receiveText);
    add('quantity', variantCount || (!variantHasAmount && !ambiguousAmount ? quantity(title, kind) || quantity(receiveText, kind) : ''));
    if (kind === 'seeds') add('packWeight', packWeight(variantText) || (!variantHasAmount && !ambiguousAmount ? packWeight(title) || packWeight(receiveText) : ''));
    add('shippingSize', shippingSize(variantText, kind, true) || shippingSize(title, kind, true) || only(receiveLines.map(line => shippingSize(line, kind, false)).filter(Boolean)));
    const rootCaveat = /\b(?:may|can|could)\s+(?:arrive|be\s+(?:shipped|received))\b[^.!?\n]{0,160}\b(?:minimal(?:\s+(?:to|or)\s+no)?|few|no)\s+(?:established\s+)?roots?\b/i.test(description);
    if ((kind === 'plant' || kind === 'cutting') && !rootCaveat) add('rooting', rooting(variantText, labels) || rooting(title, labels) || rooting(receiveText, labels));
    const potText = `${title}\n${description}`;
    if (['plant', 'cutting', 'leaf', 'stem', 'bulb'].includes(kind)) {
      const qualifiedBareRoot = description.match(/\b(?:usually|typically|generally)\s+(?:shipped|sent|supplied)\s+bare[ -]?root\b|\bmost\s+plants\s+are\s+shipped\s+bare[ -]?root\b/i)?.[0];
      const dormantBareRoot = description.match(/\bdormant\s+bare[ -]?root\s+(?:bulbs?|plants?|cuttings?)\b/i)?.[0];
      if (['plant', 'cutting', 'bulb'].includes(kind) && /\bbare[ -]?root(?:ed)?\b/i.test(title)) add('shippingForm', qualifiedBareRoot || dormantBareRoot || labels.bareRoot);
      if (/\b(?:pot|container)(?:\s+(?:and|or)\s+soil)?\s+(?:(?:is|are)\s+)?not\s+included\b|\bno\s+(?:pot|container)(?:\s+(?:and|or)\s+soil)?\s+(?:(?:is|are)\s+)?included\b|\bno\s+soil\s+(?:or|and)\s+(?:pot|container)\s+(?:(?:is|are)\s+)?included\b|\bwithout\s+(?:a\s+)?pot\b/i.test(potText)) add('pot', labels.potNotIncluded);
      else {
        const inPot = new RegExp(`\\b(?:rooted|planted|shipped|supplied|are|is)\\s+in\\s+(?:a\\s+)?(${measure}\\s*(?:nursery\\s+|growers?['’]?\\s+)?pots?)\\b`, 'gi');
        const containers = uniqueMatches(potText, inPot, match => {
          const before = potText.slice(Math.max(0, match.index - 35), match.index);
          return /(?:should|must|can|may|could)\s+(?:be\s+)?$|recommend/i.test(before) ? '' : match[1];
        }).filter(Boolean);
        const directTitlePot = title.match(new RegExp(`\\bin\\s+(?:a\\s+)?(${measure}\\s*(?:nursery\\s+|growers?['’]?\\s+)?pots?)\\b`, 'i'))?.[1];
        if (directTitlePot && !/\b(?:grows?|growing|prefers?|suitable|recommended|repot|transplant)\b/i.test(title)) containers.push(directTitlePot);
        const containerSizes = containers.map(value => value.toLowerCase().replace(/inches|inch|[\"”″]/g, 'in').replace(/[\s-]+/g, ''));
        const pot = new Set(containerSizes).size === 1 ? containers[0] : '';
        if (pot) add('pot', pot);
        else if (/\b(?:pot|container)\s+(?:is\s+)?included\b|\bincludes\s+(?:a\s+)?(?:nursery\s+)?pot\b/i.test(`${title}\n${receiveText}`)) add('pot', labels.potIncluded);
      }
    }
    const normalize = value => value.toLowerCase().replace(/\s+/g, '');
    const repeatsFact = receive.some(fact => normalize(fact.value) === normalize(variantText));
    if (variantText && variantText !== title && variantText.length <= 100 && !repeatsFact) add('selection', variantText);
    return { kind, receive, growing: ['other', 'mixed'].includes(kind) ? [] : careFacts(description, labels) };
  }

  const api = { plainText, classify, parseProductFacts };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  scope.FaithFolkProductInsights = api;
  if (typeof window === 'undefined' || !window.customElements || customElements.get('ff-product-insights')) return;

  customElements.define('ff-product-insights', class extends HTMLElement {
    connectedCallback() {
      try { this.factsData = JSON.parse(this.querySelector('script[data-product-facts]')?.textContent || '{}'); }
      catch { this.hidden = true; return; }
      this.productInfo = this.closest('product-info');
      this.onSelectionChange = event => {
        if (event.target.closest('variant-selects')) {
          this.setAttribute('data-pending', '');
          this.setAttribute('aria-busy', 'true');
        } else if (event.target.matches?.('input[name="id"]') && event.target.value === '') {
          this.updateVariant(null);
        }
      };
      this.productInfo?.addEventListener('change', this.onSelectionChange);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event?.data?.sectionId) !== String(this.dataset.sectionId)) return;
          this.updateVariant(event.data.variant);
        });
      }
      this.render();
    }

    disconnectedCallback() {
      this.unsubscribeVariant?.();
      this.productInfo?.removeEventListener('change', this.onSelectionChange);
    }

    updateVariant(variant) {
      this.removeAttribute('data-pending');
      this.removeAttribute('aria-busy');
      this.factsData.selectedVariant = variant || null;
      if (!variant) { this.hidden = true; return; }
      this.render();
    }

    renderRegion(selector, sectionSelector, facts) {
      const container = this.querySelector(selector);
      if (!container) return;
      const section = this.querySelector(sectionSelector) || container;
      section.hidden = facts.length === 0;
      const list = this.ownerDocument.createElement('dl');
      list.className = 'ff-product-facts';
      for (const fact of facts) {
        const row = this.ownerDocument.createElement('div');
        row.className = 'ff-product-facts__row';
        row.dataset.fact = fact.key;
        const label = this.ownerDocument.createElement('dt');
        const value = this.ownerDocument.createElement('dd');
        label.textContent = fact.label;
        value.textContent = fact.value;
        row.append(label, value);
        list.append(row);
      }
      container.replaceChildren(list);
    }

    render() {
      const facts = parseProductFacts(this.factsData);
      this.dataset.kind = facts.kind;
      this.renderRegion('[data-receive-facts]', '[data-receive-section]', facts.receive);
      this.renderRegion('[data-growing-facts]', '[data-growing-section]', facts.growing);
      this.hidden = !facts.receive.length && !facts.growing.length;
    }
  });
})(typeof globalThis !== 'undefined' ? globalThis : this);
